import { inject, Injectable } from '@angular/core';

import { format } from 'date-fns';
import { Observable } from 'rxjs';
import {
  BackendService,
  HttpOptions,
} from '@ipg/ngx-frontend-toolset';
import {
  ClientEventConflictResponseModel,
  ClientEventResponseModel,
} from '../../../models/clients.model';
import {
  CopyDayRequest,
  CopyEventsResponse,
  CopyWeekRequest,
} from '../models/event-operations.model';

@Injectable({
  providedIn: 'root',
})
export class ClientEventService {
  private readonly _backend = inject(BackendService);

  getByRange(
    clientId: string,
    range: { start: Date; end: Date }
  ): Observable<ClientEventResponseModel[]> {
    const dateFormat = 'yyyy-MM-dd';
    const startStr = format(range.start, dateFormat);
    const endStr = format(range.end, dateFormat);

    return this._backend.get(`/clients/${clientId}/events`, {
      params: {
        start: startStr,
        end: endStr,
      },
    });
  }

  get(
    clientId: string,
    eventId: string,
    options?: HttpOptions
  ): Observable<ClientEventResponseModel> {
    return this._backend.get(`/clients/${clientId}/events/${eventId}`, options);
  }

  create(clientId: string, data: unknown, options?: HttpOptions): Observable<string> {
    return this._backend.post(`/clients/${clientId}/events`, data, options);
  }

  createFromTemplate(
    clientId: string,
    data: unknown,
    options?: HttpOptions
  ): Observable<string> {
    return this._backend.post(
      `/clients/${clientId}/events/from-template`,
      data,
      options
    );
  }

  update(clientId: string, eventId: string, data: unknown, options?: HttpOptions) {
    return this._backend.put(`/clients/${clientId}/events/${eventId}`, data, options);
  }

  delete(clientId: string, eventId: string, options?: HttpOptions) {
    return this._backend.delete(`/clients/${clientId}/events/${eventId}`, options);
  }

  createMediaEvent(clientId: string, data: unknown, options?: HttpOptions): Observable<string> {
    return this._backend.post(`/clients/${clientId}/events/media`, data, options);
  }

  getConflicts(clientId: string): Observable<ClientEventConflictResponseModel[]> {
    return this._backend.get(`/clients/${clientId}/events/conflicts`);
  }

  copyDay(clientId: string, data: CopyDayRequest): Observable<CopyEventsResponse> {
    return this._backend.post(`/clients/${clientId}/events/copy-day`, data);
  }

  copyWeek(clientId: string, data: CopyWeekRequest): Observable<CopyEventsResponse> {
    return this._backend.post(`/clients/${clientId}/events/copy-week`, data);
  }
}
